import * as feedActions from '../actions/feed.actions';
import * as postActions from '../actions/post.actions';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { Store, select } from '@ngrx/store';
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  createPostQuickStoryReport,
  createPostQuickStoryReportFail,
} from '../actions/post.actions';
import { selectFeedPosts, selectFeedPostsByIds, selectUser } from '../reducers';

import { Injectable } from '@angular/core';
import { PostService } from '../services/post.service';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { normalizeCategoriesResponse } from 'src/app/api/normalize/post.normalize';
import {
  isShadowPostId,
  realPostId,
} from 'src/app/shared/utils/shadow-post.utils';

// Replace with the actual path to your service

@Injectable()
export class PostEffects {
  /**
   * Load list of widgets for a feed
   */
  togglePostsSelect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(postActions.togglePostSelect),
        exhaustMap(
          (
            action, // Use exhaustMap to handle one action at a time
          ) =>
            of(action).pipe(
              withLatestFrom(
                this.store.pipe(select(selectUser)),
                this.store.pipe(select(selectFeedPostsByIds([action.postId]))),
              ),
              mergeMap(([action, user, posts]) => {
                // console.log(action);

                // The clicked row may be a display-only shadow copy; send the
                // real post id to the backend so select/unselect resolves.
                const realPosts = posts.map((post) => ({
                  ...post,
                  id: realPostId(post.id),
                }));

                if (action.selected) {
                  return this.postService
                    .selectPosts(user.id, action.feedId, realPosts)
                    .pipe(
                      // Uncomment and modify the map below when you have the selectPostsSuccess action
                      // map((response) => postActions.selectPostsSuccess({ response })),
                      catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                    );
                } else {
                  return this.postService
                    .deSelectPosts(user.id, action.feedId, realPosts)
                    .pipe(
                      // Uncomment and modify the map below when you have the selectPostsSuccess action
                      // map((response) => postActions.selectPostsSuccess({ response })),
                      catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                    );
                }
              }),
            ),
        ),
      ),
    { dispatch: false },
  );

  togglePostsSelectAll$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.selectAllPosts),
        exhaustMap(
          (
            action, // Use exhaustMap to handle one action at a time
          ) =>
            of(action).pipe(
              withLatestFrom(
                this.store.pipe(select(selectUser)),
                this.store.pipe(select(selectFeedPosts)),
              ),
              mergeMap(([action, user, posts]) => {
                const postsArray = [...posts.values()].filter(
                  // Skip parents and display-only shadow copies — only real,
                  // selectable posts should be sent to select/unselect.
                  (post) => !post.is_parent && !isShadowPostId(post.id),
                );
                if (action.selected) {
                  return this.postService
                    .selectPosts(user.id, action.feedId, postsArray)
                    .pipe(
                      // Uncomment and modify the map below when you have the selectPostsSuccess action
                      // map((response) => postActions.selectPostsSuccess({ response })),
                      catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                    );
                } else {
                  return this.postService
                    .deSelectPosts(user.id, action.feedId, postsArray)
                    .pipe(
                      // Uncomment and modify the map below when you have the selectPostsSuccess action
                      // map((response) => postActions.selectPostsSuccess({ response })),
                      catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                    );
                }
              }),
            ),
        ),
      ),
    { dispatch: false },
  );

  setPostRelatedPosts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postActions.setPostRelatedPosts),
      exhaustMap(
        (
          action, // Use exhaustMap to handle one action at a time
        ) =>
          of(action).pipe(
            mergeMap((action) => {
              return this.postService
                .addRelatedPosts(
                  action.postId,
                  action.relatedPostIds,
                  action.feedId,
                )
                .pipe(
                  // Uncomment and modify the map below when you have the selectPostsSuccess action
                  map((post) =>
                    postActions.setPostRelatedPostsSuccess({ post: post }),
                  ),
                  catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                );
            }),
          ),
      ),
    ),
  );

  loadOnePost$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postActions.loadPost),
      exhaustMap(
        (
          action, // Use exhaustMap to handle one action at a time
        ) =>
          of(action).pipe(
            mergeMap((action) => {
              return this.postService
                .getPost(action.postId, action.feedId)
                .pipe(
                  // Uncomment and modify the map below when you have the selectPostsSuccess action
                  map((post) => postActions.loadPostSuccess({ post: post })),
                  catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                );
            }),
          ),
      ),
    ),
  );

  createPostSynopsis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postActions.createSynopsis),
      exhaustMap(
        (
          action, // Use exhaustMap to handle one action at a time
        ) =>
          of(action).pipe(
            mergeMap((action) => {
              return this.postService
                .createSynopsis(
                  action.postId,
                  action.languageId,
                  action.title,
                  action.synopsis,
                  action.queryId,
                )
                .pipe(
                  // Uncomment and modify the map below when you have the selectPostsSuccess action
                  map((post) =>
                    postActions.createSynopsisSuccess({ post: post }),
                  ),
                  catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                );
            }),
          ),
      ),
    ),
  );

  generateAISynopsis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postActions.generateAISynopsis),
      exhaustMap(
        (
          action, // Use exhaustMap to handle one action at a time
        ) =>
          of(action).pipe(
            mergeMap((action) => {
              return this.postService
                .generateAISynopsis(action.postId, action.languageId)
                .pipe(
                  // Uncomment and modify the map below when you have the selectPostsSuccess action
                  map((response) =>
                    postActions.generateAISynopsisSuccess({
                      post: response.post[0],
                    }),
                  ),
                  catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                );
            }),
          ),
      ),
    ),
  );

  updatePostSynopsis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postActions.updateSynopsis),
      exhaustMap(
        (
          action, // Use exhaustMap to handle one action at a time
        ) =>
          of(action).pipe(
            mergeMap((action) => {
              return this.postService
                .updateSynopsis(
                  action.postId,
                  action.id,
                  action.title,
                  action.synopsis,
                )
                .pipe(
                  // Uncomment and modify the map below when you have the selectPostsSuccess action
                  map((postSynopsis) =>
                    postActions.updateSynopsisSuccess({
                      postId: action.postId,
                      postSynopsis: postSynopsis,
                    }),
                  ),
                  catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                );
            }),
          ),
      ),
    ),
  );

  deletePostSynopsis$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postActions.deleteSynopsis),
      exhaustMap(
        (
          action, // Use exhaustMap to handle one action at a time
        ) =>
          of(action).pipe(
            mergeMap((action) => {
              return this.postService
                .deleteSynopsis(action.postId, action.id)
                .pipe(
                  // Uncomment and modify the map below when you have the selectPostsSuccess action
                  map(() =>
                    postActions.deleteSynopsisSuccess({
                      postId: action.postId,
                      id: action.id,
                    }),
                  ),
                  catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                );
            }),
          ),
      ),
    );
  });

  updatePostTranslation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postActions.updatePostTranslation),
      exhaustMap(
        (
          action, // Use exhaustMap to handle one action at a time
        ) =>
          of(action).pipe(
            mergeMap((action) => {
              return this.postService
                .updatePostTranslation(
                  action.postId,
                  action.languageId,
                  action.title,
                  action.content,
                )
                .pipe(
                  // Uncomment and modify the map below when you have the selectPostsSuccess action
                  map((post) =>
                    postActions.updatePostTranslationSuccess({ post: post }),
                  ),
                  catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                );
            }),
          ),
      ),
    );
  });

  setPostSentiment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postActions.setPostSentiment),
      exhaustMap(
        (
          action, // Use exhaustMap to handle one action at a time
        ) =>
          of(action).pipe(
            mergeMap((action) => {
              return this.postService
                .setPostSentiment(
                  action.feedId,
                  // Defensive: never let a display-only shadow id reach the backend
                  realPostId(action.postId),
                  action.sentiment,
                )
                .pipe(
                  // Uncomment and modify the map below when you have the selectPostsSuccess action
                  map((response) =>
                    postActions.setPostSentimentSuccess({
                      post: response.post[0],
                    }),
                  ),
                  catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
                );
            }),
          ),
      ),
    );
  });

  applyPostAlgorithm$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postActions.applyPostAlgorithm),
      exhaustMap((action) =>
        of(action).pipe(
          mergeMap((action) => {
            return this.postService
              .applyPostAlgorithm(
                action.feedId,
                action.postId,
                action.algorithmId,
                action.algorithmParams,
              )
              .pipe(
                // Uncomment and modify the map below when you have the selectPostsSuccess action
                map((posts) =>
                  postActions.applyPostAlgorithmSuccess({ posts }),
                ),
                catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
              );
          }),
        ),
      ),
    );
  });

  applyPostAlgorithmSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(postActions.applyPostAlgorithmSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'post-actions.msg.algorithm-processing-queued-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  applySelectedPostsAlgorithm$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postActions.applySelectedPostsAlgorithm),
      exhaustMap((action) =>
        of(action).pipe(
          mergeMap((action) => {
            return this.postService
              .applySelectedPostsAlgoriithm(
                action.feedId,
                action.algorithmId,
                action.algorithmParams,
              )
              .pipe(
                map((posts) =>
                  postActions.applySelectedPostsAlgorithmSuccess({
                    feedId: action.feedId,
                    posts: posts.flat(),
                  }),
                ),
                catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
              );
          }),
        ),
      ),
    );
  });

  createPostQuickStoryReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createPostQuickStoryReport),
      switchMap((action) =>
        this.postService
          .createQuickStoryReport(
            action.postId,
            action.languageId,
            action.feedId,
          )
          .pipe(
            map((postsResponse) =>
              postActions.createPostQuickStoryReportSuccess({
                post: postsResponse,
              }),
            ),
            catchError((error) =>
              of(
                createPostQuickStoryReportFail({
                  postId: action.postId,
                  error,
                }),
              ),
            ),
          ),
      ),
    );
  });

  updatePostQuickStoryReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postActions.updatePostQuickStoryReport),
      switchMap((action) =>
        this.postService
          .updateQuickStoryReport(action.postId, action.languageId, action.html)
          .pipe(
            map((postsResponse) =>
              postActions.updatePostQuickStoryReportSuccess({
                post: postsResponse,
              }),
            ),
            catchError((error) =>
              of(
                postActions.updatePostQuickStoryReportFail({
                  postId: action.postId,
                  error,
                }),
              ),
            ),
          ),
      ),
    );
  });

  translatePost$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(postActions.translatePost),
      switchMap((action) =>
        this.postService
          .translatePost(
            action.postId,
            action.languageId,
            action.title,
            action.content,
          )
          .pipe(
            map((postResponse) =>
              postActions.translatePostSuccess({
                postId: action.postId,
                languageId: action.languageId,
                title: postResponse.title,
                content: postResponse.content,
              }),
            ),
            catchError((error) => of(postActions.translatePostFail({ error }))),
          ),
      ),
    );
  });

  translatePostSucess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(postActions.updatePostTranslationSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'post-actions.msg.content-translation-saved-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  transitionToWorkflowStage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postActions.transitionToWorkflowStage),
      switchMap((action) =>
        this.postService
          .transitionToWorkflowStage(
            // Defensive: never let a display-only shadow id reach the backend
            realPostId(action.postId),
            action.workflowId,
            action.workflowStageId,
            action.feedId,
          )
          .pipe(
            map((post) =>
              postActions.transitionToWorkflowStageSuccess({ post }),
            ), // Use `map` to emit the success action
            catchError((error) =>
              of(postActions.transitionToWorkflowStageFail({ error })),
            ), // Handle errors properly
          ),
      ),
    ),
  );

  updatePostsUserCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postActions.updatePostsUserCategories),
      mergeMap(({ postIds, categories, updateOperation }) =>
        this.postService
          .assignCategoryTree(categories, postIds, updateOperation)
          .pipe(
            map((response) => {
              const mappedCategories = normalizeCategoriesResponse(
                response?.categories,
                postIds,
              );

              return postActions.updatePostsUserCategoriesSuccess({
                postIds,
                categories: mappedCategories,
              });
            }),
            catchError((error) =>
              of(postActions.updatePostsUserCategoriesFail({ error })),
            ),
          ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private postService: PostService,
    private store: Store,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {}
}
